import type {
  PartnerInjectionProfile,
  PartnerInjectionProfileCreate,
  PartnerInjectionProfileUpdate,
} from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import type { LinkAssetPayload, BulkLinkPayloads } from '../api-utils';
import { linkMultipleAssets } from '../api-utils';

type SingleAssetLinkType = '';
type ArrayAssetLinkType = 'injectionSites' | 'tags';

export const injectionProfiles = {
  search: {
    queryKey: 'partner-management-service/injection-profiles',
    configurationType: 'no-cache' as const,
    fn: async () => {
      const { data } = await GatewayAxiosClient.get<PartnerInjectionProfile[]>(
        '/partner-management-proxy/api/v1/partners/injectionProfile',
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/injection-profiles/id',
    configurationType: 'no-cache' as const,
    fn: async (identifier: string) => {
      const { data } = await GatewayAxiosClient.get<PartnerInjectionProfile>(
        `/partner-management-proxy/api/v1/partners/injectionProfile/${identifier}`,
      );

      return data;
    },
  },
  create: async (payload: PartnerInjectionProfileCreate) => {
    const { data } = await GatewayAxiosClient.post<PartnerInjectionProfile>(
      `/partner-management-proxy/api/v1/partners/injectionProfile`,
      payload,
    );

    return { data, invalidateQueryKeys: [['partner-management-service/injection-profiles']] };
  },
  update: async ({
    payload,
    injectionProfileId,
  }: {
    payload: PartnerInjectionProfileUpdate;
    injectionProfileId: string;
  }) => {
    const { data } = await GatewayAxiosClient.put<PartnerInjectionProfile>(
      `/partner-management-proxy/api/v1/partners/injectionProfile/${injectionProfileId}`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/injection-profiles'],
        ['partner-management-service/injection-profiles/id', injectionProfileId],
      ],
    };
  },
  link: async ({
    injectionProfileId,
    payload,
  }: {
    injectionProfileId: string;
    payload: LinkAssetPayload<SingleAssetLinkType | ArrayAssetLinkType>;
  }) => {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/injectionProfile` +
        `/${injectionProfileId}/${payload.assetType}/${payload.assetId}`,
    });

    return {
      invalidateQueryKeys: [
        ['partner-management-service/injection-profiles'],
        ['partner-management-service/injection-profiles/id', injectionProfileId],
      ],
    };
  },
  bulkLink: async ({
    injectionProfile,
    payloads,
  }: {
    injectionProfile: PartnerInjectionProfile;
    payloads: BulkLinkPayloads<SingleAssetLinkType, ArrayAssetLinkType>;
  }) => {
    return await linkMultipleAssets<SingleAssetLinkType, ArrayAssetLinkType, PartnerInjectionProfile>(
      payloads,
      {
        injectionSites: injectionProfile.injectionSiteIds,
        tags: injectionProfile.tagIds,
      },
      (payload) => injectionProfiles.link({ injectionProfileId: injectionProfile.injectionProfileId, payload }),
    );
  },
};
